
import { useState, useEffect } from "react";
import { Package, DollarSign, Calendar, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend
} from "recharts";
import DashboardLayout from "../../components/DashboardLayout";
import DateRangePicker from "../../components/DateRangePicker";
import { reportsApi } from "@/lib/api";
import { formatCurrencyFull, localToday } from "@/lib/data";

const fmt = (n: number) => formatCurrencyFull(n);

function KpiCard({ label, value, sub, icon: Icon, color, bg }: any) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-stone-100`}>
      <div className={`w-9 h-9 rounded-lg ${color} bg-white/80 flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs font-medium text-stone-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-stone-800">{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}

const PIE_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#3b82f6", "#ef4444", "#06b6d4", "#8b5cf6", "#ec4899"];

export default function PackageAnalyticsPage() {
  const today = localToday();
  const firstOfYear = today.slice(0, 4) + "-01-01";

  const [fromDate, setFromDate] = useState(firstOfYear);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await reportsApi.packageAnalytics({ from_date: fromDate, to_date: toDate });
      setData(res.data?.data);
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [fromDate, toDate]);

  const packages: any[] = data?.packages ?? [];
  const summary = data?.summary ?? {};

  const totalPackages  = summary.total_packages ?? 0;
  const totalBookings  = summary.total_bookings ?? 0;
  const totalRevenue   = summary.total_revenue ?? 0;
  // Backend stores no-package count in summary.events_without_package
  const noPackageCount = summary.events_without_package ?? 0;

  const barData = packages.map((p: any) => ({
    name: (p.name?.length ?? 0) > 18 ? p.name.slice(0, 17) + "…" : p.name,
    bookings: Number(p.total_bookings ?? 0),
    revenue: Number(p.total_revenue ?? 0),
  }));

  const pieData = packages
    .filter((p: any) => Number(p.total_bookings ?? 0) > 0)
    .map((p: any) => ({
      name: p.name,
      value: Number(p.total_bookings ?? 0),
    }));

  if (noPackageCount > 0) {
    pieData.push({ name: "No Package", value: noPackageCount });
  }

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-600" />
            <h1 className="text-lg font-bold text-stone-800">Package Analytics</h1>
          </div>
          <DateRangePicker
            fromDate={fromDate} toDate={toDate}
            onFromChange={setFromDate} onToChange={setToDate}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-stone-400 text-sm">Loading…</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard label="Total Packages" value={totalPackages} sub="active packages" icon={Package} color="text-indigo-600" bg="bg-indigo-50" />
              <KpiCard label="Package Events" value={totalBookings} sub="events with packages" icon={Calendar} color="text-blue-600" bg="bg-blue-50" />
              <KpiCard label="Total Revenue" value={fmt(totalRevenue)} sub="from package events" icon={DollarSign} color="text-emerald-600" bg="bg-emerald-50" />
              <KpiCard label="No-Package Events" value={noPackageCount} sub="events without package" icon={TrendingUp} color="text-amber-600" bg="bg-amber-50" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Bar chart: bookings per package */}
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <h2 className="text-sm font-semibold text-stone-700 mb-4">Bookings per Package</h2>
                {barData.length === 0 ? (
                  <div className="flex items-center justify-center h-48 text-stone-400 text-sm">No package data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={barData} layout="vertical" barCategoryGap="25%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip formatter={(v: any) => [v, "Bookings"]} />
                      <Bar dataKey="bookings" fill="#6366f1" radius={[0, 4, 4, 0]}>
                        {barData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Pie chart: share */}
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <h2 className="text-sm font-semibold text-stone-700 mb-4">Booking Share</h2>
                {pieData.length === 0 ? (
                  <div className="flex items-center justify-center h-48 text-stone-400 text-sm">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ percent }: any) => percent ? `${(percent * 100).toFixed(0)}%` : ""}
                        labelLine={false}
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: any, name: any) => [v, name]} />
                      <Legend formatter={(v) => v} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Revenue per package table */}
            <div className="bg-white rounded-xl border border-stone-200 p-4">
              <h2 className="text-sm font-semibold text-stone-700 mb-3">Package Revenue Breakdown</h2>
              {packages.length === 0 ? (
                <p className="text-sm text-stone-400">No package data for this period</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100">
                        <th className="text-left py-2 text-xs font-semibold text-stone-500">Package</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Bookings</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Total Revenue</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Avg Revenue</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-50">
                      {packages.map((p: any, i: number) => {
                        const rev      = Number(p.total_revenue ?? 0);
                        const bookings = Number(p.total_bookings ?? 0);
                        const share    = totalRevenue > 0 ? (rev / totalRevenue) * 100 : 0;
                        return (
                          <tr key={p.id ?? i} className="hover:bg-stone-50">
                            <td className="py-2.5">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                                <span className="font-medium text-stone-800">{p.name}</span>
                              </div>
                            </td>
                            <td className="py-2.5 text-right text-stone-600">{bookings}</td>
                            <td className="py-2.5 text-right font-semibold text-stone-800">{fmt(rev)}</td>
                            <td className="py-2.5 text-right text-stone-500">{bookings > 0 ? fmt(rev / bookings) : "—"}</td>
                            <td className="py-2.5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${share}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                                </div>
                                <span className="text-xs text-stone-400 w-8 text-right">{share.toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {noPackageCount > 0 && (
                        <tr className="bg-stone-50/50">
                          <td className="py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full bg-stone-300 flex-shrink-0" />
                              <span className="text-stone-500 italic">No Package</span>
                            </div>
                          </td>
                          <td className="py-2.5 text-right text-stone-400">{noPackageCount}</td>
                          <td className="py-2.5 text-right text-stone-400">—</td>
                          <td className="py-2.5 text-right text-stone-400">—</td>
                          <td />
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-stone-200 bg-stone-50">
                        <td className="py-2.5 font-bold text-stone-800">Total</td>
                        <td className="py-2.5 text-right font-bold text-stone-700">{totalBookings}</td>
                        <td className="py-2.5 text-right font-bold text-emerald-700">{fmt(totalRevenue)}</td>
                        <td className="py-2.5 text-right text-stone-500">
                          {totalBookings > 0 ? fmt(totalRevenue / totalBookings) : "—"}
                        </td>
                        <td className="py-2.5 text-right text-xs text-stone-400">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
